import type { TextWithFigureComponent } from "../../types";
import { RuntimeImage } from "../../runtime/RendererRuntime";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";

/**
 * Two-column editorial block: structured copy on one side, a figure on the
 * other (stacks on mobile). Unlike {@link TextWithSideImageRenderer} (which is
 * tailored to governor bios) this is generic: an optional highlighted intro
 * paragraph, then a list of labelled items each rendered as a bold lead-in
 * ("Toucher.", "Regarder.", …) followed by its explanation — exactly the
 * "méthode TRIE" pattern. Falls back to plain `paragraphs` when no items are
 * supplied. Image side defaults to the right; set `imagePosition: "left"` to flip.
 */
export function TextWithFigureRenderer({ component }: { component: TextWithFigureComponent }) {
  const { intro, items, paragraphs, imageSrc, imageAlt = "", imagePosition = "right" } = component.content ?? ({} as never);
  const { src, options } = resolveImageValue(imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);

  const figure = src ? (
    <div className="md:shrink-0 md:basis-[42%] lg:basis-[44%]">
      <RuntimeImage
        src={src}
        alt={imageAlt}
        className={mergeClassNames("h-full w-full rounded-md object-cover ring-1 ring-black/10", overrideProps.className)}
        style={mergeStyles(objectPositionStyle, overrideProps.style)}
        priority
      />
    </div>
  ) : null;

  const text = (
    <div className="min-w-0 flex-1">
      {intro ? <p className="text-base xl:text-lg leading-7 font-medium text-[#005CA9]">{intro}</p> : null}
      {(items ?? []).length > 0 ? (
        <div className={intro ? "mt-4 space-y-3" : "space-y-3"}>
          {items!.map((it, idx) => (
            <p key={idx} className="text-base xl:text-lg leading-7 text-[#0F2849]">
              <strong className="text-[#005CA9]">{it.lead}</strong> {it.text}
            </p>
          ))}
        </div>
      ) : null}
      {(paragraphs ?? []).length > 0 ? (
        <div className={intro || (items ?? []).length ? "mt-4 space-y-4" : "space-y-4"}>
          {paragraphs!.map((p, idx) => (
            <p key={idx} className="text-base xl:text-lg leading-7 text-[#0F2849]">
              {p}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <section>
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-8">
        {imagePosition === "left" ? (
          <>
            {figure}
            {text}
          </>
        ) : (
          <>
            {text}
            {figure}
          </>
        )}
      </div>
    </section>
  );
}
